import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  ADDITIONAL_FIELDS,
  ADDRESS_FIELDS,
  SEARCH_DEBOUNCE_MS,
  TRANSLATION_KEYS,
} from "../../../../classes/constants/searchConstant";
import { useAppContext } from "../../../../classes/contexts/AppContext";
import { GENDER } from "../../../../classes/enums/PatientInfoEnum";
import { DataPermissions } from "../../../../classes/enums/PermissionsEnum";
import useTranslation from "../../../../hooks/i18n/useTranslation";
import { useAuth } from "../../../../hooks/useAuth";
import { useSearch } from "../../../../hooks/useSearch";
import PatientFormModal from "../patientModal/PatientFormModal";
import "./SearchPanel.css";

import { MAX_DELTA_YEARS } from "../../../../classes/constants/validationConstants";
import IconsEnum from "../../../../classes/enums/IconsEnum";
import ButtonWithIcon from "../../../../components/button/ButtonWithIcon";
import DatePickerComponent from "../../../../components/datepicker/Datepicker";
import IconButton from "../../../../components/iconButton/IconButton";
import DownArrowIcon from "../../../../components/icons/DownArrowIcon";
import UpArrowIcon from "../../../../components/icons/UpArrowIcon";
import Select from "../../../../components/select/Select";
import TextBox from "../../../../components/textBox/TextBox";
import { useRouteState } from "../../../../hooks/useRouteState";

function SearchPanel({ onSearch, refreshPatients }) {
  const { t } = useTranslation();
  const culture = useSelector(state => state.i18n.culture);
  const [isExpanded, setIsExpanded] = useState(false);
  const { showPopup } = useAppContext();
  const { checkPermissions } = useAuth();
  const [datePickerValue, setDatePickerValue] = useState(null);
  const maxDate = useMemo(() => new Date(), []);
  const minDate = useMemo(() => {
    const date = new Date();
    date.setFullYear(date.getFullYear() - MAX_DELTA_YEARS);
    return date;
  }, []);

  const {
    searchCriteria,
    handleSearchChange,
    handleDateChangeInSearch,
    clearAllFilters,
  } = useSearch(onSearch);

  const { navigateToTwinCreation } = useRouteState();

  const handleDatePickerChange = timestamp => {
    // Update the separate date picker state
    setDatePickerValue(timestamp);
    // Pass the value to the search criteria handler
    handleDateChangeInSearch(timestamp);
  };

  const handleClearFilters = () => {
    // Reset the date picker value
    setDatePickerValue(null);
    clearAllFilters();
  };

  // Check if user has permission to create patients
  const canCreatePatient = useMemo(
    () => checkPermissions([DataPermissions.CREATE_PATIENT]),
    [checkPermissions]
  );

  // Memoize options to prevent unnecessary re-renders
  const genderOptions = useMemo(
    () => [
      { label: t(TRANSLATION_KEYS.genderOption1), value: GENDER.MALE },
      { label: t(TRANSLATION_KEYS.genderOption2), value: GENDER.FEMALE },
    ],
    [t]
  );

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      onSearch({ ...searchCriteria });
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(delayDebounceFn);
  }, [searchCriteria, onSearch]);

  const handleAddPatient = () => {
    showPopup(
      <PatientFormModal
        initialData={{ ...searchCriteria }}
        onPersonalDataSave={refreshPatients}
        onNavigateToDigitalTwin={navigateToTwinCreation}
      />
    );
  };

  // Handle address field changes directly
  const handleAddressFieldChange = e => {
    const { name, value } = e.target;

    handleSearchChange({
      target: {
        name: `address.${name}`, // Add the 'address.' prefix to correctly identify address fields
        value,
      },
    });
  };

  const getAddressFieldValue = field => {
    return searchCriteria?.address?.[field] || "";
  };

  return (
    <div className="App-module-patients-search">
      <label>{t("5")}</label>
      <div className="App-module-patients-main-filters">
        <div className="App-patient-filters-grid">
          <TextBox
            placeholder={t(TRANSLATION_KEYS.first_name)}
            name="first_name"
            value={searchCriteria.first_name || ""}
            onChange={handleSearchChange}
          />
          <TextBox
            placeholder={t(TRANSLATION_KEYS.last_name)}
            name="last_name"
            value={searchCriteria.last_name || ""}
            onChange={handleSearchChange}
          />
          <DatePickerComponent
            handleChange={handleDatePickerChange}
            timestamp={datePickerValue}
            placeholderText={t(TRANSLATION_KEYS.birthday)}
            locale={culture}
            maxDate={maxDate}
            minDate={minDate}
          />
          <Select
            placeholder={t(TRANSLATION_KEYS.gender)}
            items={genderOptions}
            name="gender"
            selectedItem={searchCriteria.gender || ""}
            onChange={handleSearchChange}
          />
          <IconButton
            onClick={handleClearFilters}
            icon={IconsEnum.CLEARFILTER}
            noBgColor
            title={t("183")}
          />
        </div>
        {canCreatePatient && (
          <ButtonWithIcon
            value={t("50")}
            onClick={handleAddPatient}
            icon={IconsEnum.ADD}
          />
        )}
      </div>
      <div
        className={`App-module-patients-search-expandable ${
          isExpanded ? "expanded" : ""
        }`}
      >
        <div
          className="App-module-patients-search-expandable-row"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="App-module-patients-search-expandable-row-arrow">
            <div className="App-module-patients-search-expandable-row-arrow-icon">
              {isExpanded ? <UpArrowIcon /> : <DownArrowIcon />}
            </div>
          </div>
        </div>
        <div className="App-module-patients-search-row">
          {ADDRESS_FIELDS.map(field => (
            <TextBox
              key={field}
              placeholder={t(TRANSLATION_KEYS[field])}
              name={field}
              value={getAddressFieldValue(field)}
              onChange={handleAddressFieldChange} // Use the custom handler for address fields
            />
          ))}
        </div>
        <div className="App-module-patients-search-row">
          {ADDITIONAL_FIELDS.map(field => (
            <TextBox
              key={field}
              placeholder={t(TRANSLATION_KEYS[field])}
              name={field}
              value={searchCriteria[field] || ""}
              onChange={handleSearchChange}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

export default React.memo(SearchPanel);
